import {Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BaseChartComponent, type EnergyType, type Interval} from './base-chart.component';
import {BaseChartDirective} from 'ng2-charts';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {Chart, ChartOptions} from 'chart.js';
import {de} from 'date-fns/locale';
import annotationPlugin from 'chartjs-plugin-annotation';
import {CommonModule} from '@angular/common';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {getDaysInMonth} from 'date-fns';
import { environment } from '../../../../environments/environment';
import {getAverageEnergyStatus} from './average-energy-response.helper';

@Component({
  selector: 'app-grid-bar-chart',
  templateUrl: './chart.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective, CommonModule],
})
export class GridBarChartComponent extends BaseChartComponent implements OnChanges {
  @Input() override interval: Interval = 'month'; // Define input with default value
  type: EnergyType = 'grid'; // Define input with default value
  @Input() date: Date = new Date();
  @Input() data: AverageEnergyResponse | undefined;

// Override unit to kwh for month and year charts, kW for day charts
  @Input() override get unit(): string {
    switch (this.interval) {
      case 'month':
      case 'year':
        return 'kWh';
      default:
        return super.unit;
    }
  }

  override set unit(value: string) {
    // Allow external setting but ignore it since we determine unit based on batteryType
  }

  monthBarChartOptions: ChartOptions = {
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the bottom
        display: true, // Enable legend display
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        stacked: true,
        beginAtZero: true,
        border: {
          display: false,
        },
        offset: true,
      },
      x: {
        stacked: true,
        type: 'time', // Ensure time-based X-axis
        time: {
          unit: 'day', // Display days on the axis
          tooltipFormat: 'dd MM', // Tooltip format (e.g., "01 Mar")
          displayFormats: !this.isMobile()
            ? {day: 'dd MM'} // Format for the axis labels
            : {day: 'd'}, // Display only day numbers (e.g., "1", "2", "3")
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          maxRotation: 90,
          minRotation: 90,
          callback: (value) => {
            const date = new Date(value);
            return this.isMobile()
              ? date.getDate() // Return just the day number for mobile
              : date.toLocaleDateString(environment.locale, {
                  day: '2-digit',
                  month: 'numeric',
                  timeZone: this.timezone,
                });
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  yearBarChartOptions: ChartOptions = {
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom', // Position the legend at the bottom
        display: true, // Enable legend display
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        stacked: true,
        border: {
          display: false,
        },
        offset: true,
      },
      x: {
        stacked: true,
        type: 'time', // Use timescale for date-based X-axis
        time: {
          unit: 'month', // Group data by month
          tooltipFormat: 'MMM yyyy', // Show "Mar 2025" format in tooltips
          displayFormats: {
            month: 'MMM', // Display short month names (e.g., "Jan", "Feb")
            day: 'D', // Show day number (e.g., "1", "15", "30")
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          callback: (value) => {
            const date = new Date(value as string);
            return date.toLocaleDateString(environment.locale, {month: 'short', timeZone: this.timezone});
          },
        },
        border: {
          display: false,
        },
      },
    },
  };

  constructor() {
    super();
    // Register the annotation plugin
    Chart.register(annotationPlugin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Performance Grid chart received change', changes);
    if (changes['data']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return; // Exit if no data is available
    }
    this.initializeChartData();
  }

  private initializeChartData() {
    console.log('initialize Performance Chart');
    if (!this.data) {
      return; // Exit if no data is available
    }

    const datasets: any[] = [];
    const allLabels: Date[][] = [];

    // Process both positive and negative grid power
    const positiveData = getAverageEnergyStatus(this.data, 'gridPositive');
    const negativeData = getAverageEnergyStatus(this.data, 'gridNegative');

    if (!positiveData || !negativeData || !positiveData.deviceTimeValues || !negativeData.deviceTimeValues) {
      return;
    }

    // Process positive power data
    Object.entries(positiveData.deviceTimeValues).forEach(([key, timeValues]) => {
      // Type guard to ensure timeValues is an array of DateTimeValue
      if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
      // Ensure timeValues is complete
      const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date, 'zero', positiveData.timeBucket);
      // Ensure values are positive
      const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({
        ...tv,
        value: tv.value !== undefined && tv.value != null ? Math.abs(tv.value) : tv.value,
      }));

      // Extract labels from this dataset
      const datasetLabels: Date[] = [];
      completedTimeValues.forEach((item) => {
        const date = new Date(item.read ?? '');
        if (!isNaN(date.getTime())) {
          datasetLabels.push(date);
        }
      });
      allLabels.push(datasetLabels);

      this.applyIntervalMultiplier(rawTimeValues);


      const legendLabel = positiveData.deviceNames?.[key] || key;
      datasets.push({
        label: `Einkauf`,
        data: rawTimeValues.map((v) => v.value),
        borderColor: '#333333', // Green for positive
        backgroundColor: '#333333',
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.1,
        cubicInterpolationMode: 'monotone',
        spanGaps: false,
        borderWidth: 1,
        stack: 'stack1',
        base: 0,
      });

    });

    // Process negative power data
    Object.entries(negativeData.deviceTimeValues).forEach(([key, timeValues]) => {
      // Type guard to ensure timeValues is an array of DateTimeValue
      if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
      // Ensure timeValues is complete
      const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date, 'zero', negativeData.timeBucket);
      // Ensure all values are negative
      const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({
        ...tv,
        value: tv.value !== undefined && tv.value !== null ? -Math.abs(tv.value) : tv.value,
      }));

      // Extract labels from this dataset
      const datasetLabels: Date[] = [];
      completedTimeValues.forEach((item) => {
        const date = new Date(item.read ?? '');
        if (!isNaN(date.getTime())) {
          datasetLabels.push(date);
        }
      });
      allLabels.push(datasetLabels);

      this.applyIntervalMultiplier(rawTimeValues);

      const legendLabel = negativeData.deviceNames?.[key] || key;
      datasets.push({
        label: `Verkauf`,
        data: rawTimeValues.map((v) => v.value),
        borderColor: '#a0a0a0', // Red for negative #F44336 //dunkles grün: #3d6c26 //dunkles rot 9c1f18// blau: 00B6EE
        backgroundColor: '#a0a0a0',
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.1,
        cubicInterpolationMode: 'monotone',
        spanGaps: false,
        borderWidth: 1,
        stack: 'stack1',
        base: 0,
      });

    });

    // Process all labels to ensure they match
    const labels = this.processLabels(allLabels);
    this.updateChart(labels, datasets);
  }

  private updateChart(labels: any[], datasets: any[]) {
    super.updateChartType(this.interval);
    this.chartOptions.set(this.getCustomChartOptions());
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  private getCustomChartOptions() {
    switch (this.interval) {
      case 'month':
        return this.monthBarChartOptions;
      case 'year':
        return this.yearBarChartOptions;
      default:
        return this.monthBarChartOptions;
    }
  }

  private applyIntervalMultiplier(timeValues: DateTimeValue[]) {
    if (this.interval === 'month') {
      timeValues.forEach((item) => {
        item.value = (item.value || 0) * 24;
      });
      return;
    }

    if (this.interval === 'year') {
      timeValues.forEach((item) => {
        if (!item.read) {
          return;
        }

        const date = new Date(item.read);
        if (isNaN(date.getTime())) {
          return;
        }

        item.value = (item.value || 0) * getDaysInMonth(date) * 24;
      });
    }
  }

}
